import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { FiMessageCircle, FiX, FiSend } from 'react-icons/fi';

const STATUS_LABELS = {
  pending:     { label: 'Pending',     bg: '#b7860b' },
  assigned:    { label: 'Assigned',    bg: '#2471a3' },
  in_progress: { label: 'In Progress', bg: '#2471a3' },
  completed:   { label: 'Completed',   bg: '#1e8449' },
  cancelled:   { label: 'Cancelled',   bg: '#922b21' },
};

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  return `${hour % 12 || 12}:${m} ${hour < 12 ? 'AM' : 'PM'}`;
}

// Derive masked mobile from synthetic email: "919876543210@solo.lifechiai.com" → "+91 ****3210"
function maskMobile(email) {
  if (!email) return '';
  const raw = email.split('@')[0];
  if (raw.length < 4) return '+' + raw;
  const last4  = raw.slice(-4);
  const masked = raw.slice(0, -4).replace(/\d/g, '*');
  return '+' + masked + last4;
}

const SoloPatientDashboard = () => {
  const navigate = useNavigate();
  const [user]     = useState(() => JSON.parse(localStorage.getItem('pwc_user') || '{}'));

  const [bookings,    setBookings]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [expandedId,          setExpandedId]          = useState(null);
  const [chatOpen,            setChatOpen]            = useState(false);
  const [chatMessages,        setChatMessages]        = useState([]);
  const [chatInput,           setChatInput]           = useState('');
  const [chatSending,         setChatSending]         = useState(false);
  const [chatUpgradeRequired, setChatUpgradeRequired] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('pwc_token')) {
      navigate('/solo-patient/login');
      return;
    }
    (async () => {
      try {
        const res = await api.get('/solo-patient/bookings');
        setBookings(res.data.data || []);
      } catch {
        setError('Could not load your bookings. Please refresh to retry.');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('pwc_token');
    localStorage.removeItem('pwc_user');
    navigate('/solo-patient/login');
  };

  const handleSendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatSending) return;
    const history = chatMessages.slice(-10).map(m => ({ role: m.role, content: m.content }));
    setChatMessages(prev => [...prev, { role: 'user', content: text }]);
    setChatInput('');
    setChatSending(true);
    try {
      const res = await api.post('/ai/solo-patient/chat', { message: text, history });
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.data.reply }]);
    } catch (err) {
      if (err.response?.data?.code === 'PLAN_UPGRADE_REQUIRED') {
        setChatUpgradeRequired(true);
      } else {
        setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' }]);
      }
    } finally {
      setChatSending(false);
    }
  };

  const handleWithdrawConsent = async () => {
    if (!window.confirm('The centre will no longer contact you. Your booking history will not be deleted. Continue?')) return;
    try {
      await api.post('/solo-patient/consent/withdraw');
      window.alert('Consent withdrawn successfully.');
    } catch {
      window.alert('Could not withdraw consent. Please try again.');
    }
  };

  const today    = new Date().toISOString().slice(0, 10);
  const upcoming = bookings.find(b =>
    b.slotDate && b.slotDate >= today && ['pending', 'assigned', 'in_progress'].includes(b.status)
  ) || null;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <span style={s.brand}>LifeChiAI</span>
        <button style={s.logoutBtn} onClick={handleLogout}>Logout</button>
      </div>

      <div style={s.body}>
        <h1 style={s.welcome}>Welcome, {user.firstName || 'Patient'}</h1>

        {error && <div style={s.errorBox}>{error}</div>}

        {loading ? (
          <div style={s.loadingBox}>Loading your bookings…</div>
        ) : (
          <>
            {/* Upcoming session */}
            <div style={s.section}>
              <div style={s.sectionLabel}>UPCOMING SESSION</div>
              {upcoming ? (
                <div style={s.upcomingCard}>
                  <div style={s.upcomingHealer}>{upcoming.healerName || 'Your healer'}</div>
                  <div style={s.upcomingMeta}>
                    {formatDate(upcoming.slotDate)}
                    {upcoming.slotTime    && ` · ${formatTime(upcoming.slotTime)}`}
                    {upcoming.sessionType && ` · ${upcoming.sessionType}`}
                  </div>
                  <span style={{ ...s.badge, background: STATUS_LABELS[upcoming.status]?.bg || '#555' }}>
                    {STATUS_LABELS[upcoming.status]?.label || upcoming.status}
                  </span>
                  {upcoming.razorpayShortUrl && upcoming.status !== 'paid' && (
                    <a href={upcoming.razorpayShortUrl} target="_blank" rel="noopener noreferrer" style={s.upcomingPayLink}>
                      Complete payment →
                    </a>
                  )}
                </div>
              ) : (
                <div style={s.emptyCard}>No upcoming sessions.</div>
              )}
            </div>

            {/* Booking history */}
            <div style={s.section}>
              <div style={s.sectionLabel}>BOOKING HISTORY</div>
              {bookings.length === 0 ? (
                <div style={s.emptyCard}>No bookings yet.</div>
              ) : (
                <div style={s.historyList}>
                  {bookings.map(b => {
                    const isExpanded = expandedId === b._id.toString();
                    const ref = b.invoiceNumber || b._id.toString().slice(-8).toUpperCase();
                    return (
                      <div key={b._id} style={s.historyItem}>
                        <div style={s.historyRow}>
                          <div style={s.historyLeft}>
                            <div style={s.historyDate}>
                              {b.slotDate ? formatDate(b.slotDate) : formatDate(b.requestedAt)}
                            </div>
                            <div style={s.historyDetail}>
                              {[b.sessionType, b.healerName].filter(Boolean).join(' · ') || 'Session'}
                            </div>
                            {b.condition && <div style={s.historyCondition}>{b.condition}</div>}
                            {b.amountPaid != null && (
                              <button style={s.receiptToggle} onClick={() => setExpandedId(isExpanded ? null : b._id.toString())}>
                                {isExpanded ? 'Hide Receipt' : 'View Receipt'}
                              </button>
                            )}
                          </div>
                          <div style={s.historyRight}>
                            <span style={{ ...s.badge, background: STATUS_LABELS[b.status]?.bg || '#555' }}>
                              {STATUS_LABELS[b.status]?.label || b.status}
                            </span>
                            {b.amountPaid != null && (
                              <div style={s.historyAmount}>₹{b.amountPaid}</div>
                            )}
                          </div>
                        </div>
                        {b.razorpayShortUrl && b.status !== 'paid' && (
                          <a href={b.razorpayShortUrl} target="_blank" rel="noopener noreferrer" style={s.payLinkBanner}>
                            Complete payment →
                          </a>
                        )}
                        {isExpanded && (
                          <div style={s.receiptPanel}>
                            <div style={s.receiptTitle}>RECEIPT</div>
                            <div style={s.receiptRow}><span style={s.receiptLabel}>Reference</span><span style={s.receiptValue}>{b.invoiceNumber ? ref : '#' + ref}</span></div>
                            <div style={s.receiptRow}><span style={s.receiptLabel}>Date</span><span style={s.receiptValue}>{b.slotDate ? formatDate(b.slotDate) : formatDate(b.requestedAt)}</span></div>
                            <div style={s.receiptRow}><span style={s.receiptLabel}>Healer</span><span style={s.receiptValue}>{b.healerName || '—'}</span></div>
                            <div style={s.receiptRow}><span style={s.receiptLabel}>Service</span><span style={s.receiptValue}>{b.sessionType || 'Healing Session'}</span></div>
                            <div style={s.receiptRow}><span style={s.receiptLabel}>Amount</span><span style={{ ...s.receiptValue, ...s.receiptAmount }}>₹{b.amountPaid}</span></div>
                            <div style={s.receiptRow}><span style={s.receiptLabel}>Status</span><span style={{ ...s.badge, background: STATUS_LABELS[b.status]?.bg || '#555' }}>{STATUS_LABELS[b.status]?.label || b.status}</span></div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Profile footer */}
            <div style={s.profileRow}>
              <div style={s.profileName}>{[user.firstName, user.lastName].filter(Boolean).join(' ')}</div>
              <div style={s.profileMobile}>{maskMobile(user.email)}</div>
              <div style={s.consentRow}>
                <button style={s.consentBtn} onClick={handleWithdrawConsent}>
                  Withdraw Consent
                </button>
              </div>
              <div style={s.deleteNote}>
                To delete your account, contact{' '}
                <a href="mailto:support@lifechiai.com" style={s.deleteLink}>support@lifechiai.com</a>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Floating wellness chat button */}
      <button
        style={{ ...s.chatBtn, background: chatOpen ? '#0f4a3c' : '#1a6b5a' }}
        onClick={() => setChatOpen(o => !o)}
        aria-label={chatOpen ? 'Close wellness assistant' : 'Open wellness assistant'}
      >
        {chatOpen ? <FiX size={24} color="#fff" /> : <FiMessageCircle size={24} color="#fff" />}
      </button>

      {chatOpen && (
        <div style={s.chatPanel}>
          <div style={s.chatHeader}>
            <span style={s.chatTitle}>Wellness Assistant</span>
            <button style={s.chatClose} onClick={() => setChatOpen(false)}>
              <FiX size={16} color="rgba(255,255,255,0.6)" />
            </button>
          </div>

          {chatUpgradeRequired ? (
            <div style={s.chatUpgrade}>
              <div style={s.chatUpgradeText}>Upgrade to Solo Plus to access the AI wellness assistant.</div>
            </div>
          ) : (
            <>
              <div style={s.chatMsgs}>
                {chatMessages.length === 0 && (
                  <div style={s.chatEmpty}>Ask me anything about your health and wellness.</div>
                )}
                {chatMessages.map((m, i) => (
                  <div key={i} style={{ ...s.chatBubble, ...(m.role === 'user' ? s.chatBubbleUser : s.chatBubbleBot) }}>
                    {m.content}
                  </div>
                ))}
                {chatSending && <div style={{ ...s.chatBubble, ...s.chatBubbleBot, opacity: 0.5 }}>…</div>}
              </div>
              <div style={s.chatInputRow}>
                <input
                  style={s.chatInput}
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSendChat()}
                  placeholder="Ask a wellness question…"
                  maxLength={500}
                  disabled={chatSending}
                />
                <button
                  style={{ ...s.chatSendBtn, opacity: (!chatInput.trim() || chatSending) ? 0.4 : 1 }}
                  onClick={handleSendChat}
                  disabled={!chatInput.trim() || chatSending}
                >
                  <FiSend size={15} color="#fff" />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const s = {
  page:             { minHeight: '100vh', background: 'linear-gradient(135deg, #071e12 0%, #0a1f14 30%, #0c2617 60%, #071e12 100%)', fontFamily: "'Open Sans', sans-serif" },
  header:           { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.07)' },
  brand:            { fontFamily: "'Raleway', sans-serif", fontSize: '16px', fontWeight: '800', color: '#4a8f82', letterSpacing: '0.5px' },
  logoutBtn:        { background: 'none', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: 'rgba(255,255,255,0.5)', fontSize: '12px', padding: '8px 16px', cursor: 'pointer', minHeight: '44px', fontFamily: "'Open Sans', sans-serif" },
  body:             { maxWidth: '560px', margin: '0 auto', padding: '28px 20px 48px' },
  welcome:          { fontFamily: "'Raleway', sans-serif", fontSize: '24px', fontWeight: '800', color: '#fff', margin: '0 0 28px', letterSpacing: '0.3px' },
  errorBox:         { background: 'rgba(192,57,43,0.2)', border: '1px solid rgba(192,57,43,0.4)', borderRadius: '8px', padding: '14px 16px', color: '#e74c3c', fontSize: '13px', marginBottom: '20px' },
  loadingBox:       { color: 'rgba(255,255,255,0.4)', fontSize: '14px', textAlign: 'center', padding: '40px 0' },
  section:          { marginBottom: '32px' },
  sectionLabel:     { fontSize: '10px', fontWeight: '700', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.35)', fontFamily: "'Raleway', sans-serif", marginBottom: '12px' },
  upcomingCard:     { background: 'rgba(74,143,130,0.12)', border: '1px solid rgba(74,143,130,0.3)', borderRadius: '12px', padding: '20px' },
  upcomingHealer:   { fontSize: '17px', fontWeight: '700', color: '#fff', marginBottom: '6px' },
  upcomingMeta:     { fontSize: '13px', color: 'rgba(255,255,255,0.55)', marginBottom: '14px' },
  emptyCard:        { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', padding: '20px', color: 'rgba(255,255,255,0.3)', fontSize: '14px' },
  badge:            { display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', color: '#fff', letterSpacing: '0.5px' },
  historyList:      { borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.07)' },
  historyItem:      { borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(255,255,255,0.02)' },
  historyRow:       { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '14px 16px' },
  historyLeft:      { flex: 1, paddingRight: '12px' },
  historyDate:      { fontSize: '13px', fontWeight: '600', color: '#fff', marginBottom: '3px' },
  historyDetail:    { fontSize: '12px', color: 'rgba(255,255,255,0.45)', marginBottom: '3px' },
  historyCondition: { fontSize: '11px', color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' },
  historyRight:     { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', flexShrink: 0 },
  historyAmount:    { fontSize: '13px', fontWeight: '700', color: 'rgba(255,255,255,0.6)' },
  receiptToggle:    { background: 'none', border: 'none', color: 'rgba(74,143,130,0.8)', fontSize: '11px', fontWeight: '600', padding: '6px 0 0', cursor: 'pointer', fontFamily: "'Open Sans', sans-serif", letterSpacing: '0.3px' },
  receiptPanel:     { padding: '12px 16px 14px', borderTop: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.03)' },
  receiptTitle:     { fontSize: '9px', fontWeight: '700', letterSpacing: '1.8px', color: 'rgba(255,255,255,0.3)', fontFamily: "'Raleway', sans-serif", marginBottom: '10px' },
  receiptRow:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '7px' },
  receiptLabel:     { fontSize: '11px', color: 'rgba(255,255,255,0.4)' },
  receiptValue:     { fontSize: '12px', fontWeight: '600', color: 'rgba(255,255,255,0.75)' },
  receiptAmount:    { fontSize: '14px', color: '#4a8f82' },
  profileRow:       { marginTop: '8px', padding: '16px', background: 'rgba(255,255,255,0.02)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)' },
  profileName:      { fontSize: '14px', fontWeight: '600', color: 'rgba(255,255,255,0.7)', marginBottom: '4px' },
  profileMobile:    { fontSize: '12px', color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' },
  consentRow:       { marginTop: '14px' },
  consentBtn:       { background: 'none', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: 'rgba(255,255,255,0.5)', fontSize: '12px', padding: '8px 16px', cursor: 'pointer', minHeight: '44px', fontFamily: "'Open Sans', sans-serif" },
  deleteNote:       { marginTop: '10px', fontSize: '11px', color: 'rgba(255,255,255,0.25)' },
  deleteLink:       { color: 'rgba(255,255,255,0.4)', textDecoration: 'underline' },
  chatBtn:          { position: 'fixed', bottom: 24, right: 20, width: 56, height: 56, borderRadius: 28, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.4)', zIndex: 1001 },
  chatPanel:        { position: 'fixed', bottom: 92, right: 20, width: 320, maxHeight: 460, background: '#0e2a1e', border: '1px solid rgba(74,143,130,0.3)', borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', zIndex: 1000, overflow: 'hidden' },
  chatHeader:       { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)' },
  chatTitle:        { fontSize: 13, fontWeight: 700, color: '#4a8f82', fontFamily: "'Raleway', sans-serif", letterSpacing: '0.5px' },
  chatClose:        { background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' },
  chatMsgs:         { flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 160, maxHeight: 300 },
  chatEmpty:        { fontSize: 13, color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '24px 8px' },
  chatBubble:       { fontSize: 13, lineHeight: 1.5, padding: '8px 12px', borderRadius: 10, maxWidth: '88%', wordBreak: 'break-word' },
  chatBubbleUser:   { background: 'rgba(74,143,130,0.25)', color: '#fff', alignSelf: 'flex-end', borderBottomRightRadius: 2 },
  chatBubbleBot:    { background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.85)', alignSelf: 'flex-start', borderBottomLeftRadius: 2 },
  chatInputRow:     { display: 'flex', gap: 8, padding: '10px 12px', borderTop: '1px solid rgba(255,255,255,0.07)' },
  chatInput:        { flex: 1, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: '#fff', outline: 'none', fontFamily: "'Open Sans', sans-serif" },
  chatSendBtn:      { width: 36, height: 36, borderRadius: 8, background: '#1a6b5a', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  chatUpgrade:      { padding: '28px 20px', textAlign: 'center' },
  chatUpgradeText:  { fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.7 },
  upcomingPayLink:  { display: 'inline-block', marginTop: '12px', padding: '8px 18px', background: 'rgba(255,160,60,0.15)', border: '1px solid rgba(184,111,16,0.35)', borderRadius: '6px', color: '#c47a17', fontSize: '12px', fontWeight: '700', textDecoration: 'none', fontFamily: "'Raleway', sans-serif", letterSpacing: '0.3px' },
  payLinkBanner:    { display: 'block', padding: '10px 16px', borderTop: '1px solid rgba(184,111,16,0.25)', background: 'rgba(255,160,60,0.07)', color: '#c47a17', fontSize: '12px', fontWeight: '700', textDecoration: 'none', fontFamily: "'Raleway', sans-serif", letterSpacing: '0.3px' },
};

export default SoloPatientDashboard;
